import React, { useEffect, useState } from "react";
import Sidebar from "../../components/Sidebar";
import Navbar from "../../components/Navbar";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import axiosInstance from "../../../axiosInstance";
import slugify from "slugify";

const MySwal = withReactContent(Swal);

const generateSlug = (name) =>
  slugify(name || "", { lower: true, strict: true, trim: true });

export default function AdminStatesSinglePage() {
  const [loading, setLoading] = useState(true);

  // dropdown
  const [countryOptions, setCountryOptions] = useState([]);

  // list
  const [states, setStates] = useState([]);
  const [filterCountryId, setFilterCountryId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  // add form
  const [addLoading, setAddLoading] = useState(false);
  const [addForm, setAddForm] = useState({
    countryId: "",
    name: "",
    code: "",
    slug: "",
    status: true,
  });

  // edit modal
  const [editOpen, setEditOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editForm, setEditForm] = useState({
    _id: "",
    countryId: "",
    name: "",
    code: "",
    slug: "",
    status: true,
  });

  const fetchCountriesDropdown = async () => {
    try {
      const res = await axiosInstance.get("/admin/countries?page=1&limit=500&search=");
      const list = res.data.countries || res.data.Countries || [];
      setCountryOptions(list);
    } catch (e) {
      toast.error("Error loading countries");
    }
  };

  const fetchStates = async () => {
    try {
      setLoading(true);
      const url = `/admin/states?page=${currentPage}&limit=${limit}&search=${encodeURIComponent(
        searchTerm
      )}&countryId=${filterCountryId || ""}`;

      const res = await axiosInstance.get(url);
      const list = res.data.states || res.data.States || [];
      setStates(list);
      setCurrentPage(res.data.currentPage || currentPage);
      setTotalPages(res.data.totalPages || 1);
    } catch (e) {
      toast.error("Error fetching states");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCountriesDropdown();
  }, []);

  useEffect(() => {
    fetchStates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, limit]);

  const onSearch = () => {
    setCurrentPage(1);
    fetchStates();
  };

  // ADD
  const handleAddChange = (e) => {
    const { name, value } = e.target;

    if (name === "name") {
      setAddForm((p) => ({ ...p, name: value, slug: generateSlug(value) }));
      return;
    }
    setAddForm((p) => ({ ...p, [name]: value }));
  };

  const addState = async () => {
    if (!addForm.countryId) return toast.error("Please select country");
    if (!addForm.name.trim()) return toast.error("Please enter state name");

    try {
      setAddLoading(true);
      await axiosInstance.post("/admin/state", {
        countryId: addForm.countryId,
        name: addForm.name.trim(),
        code: addForm.code.trim(),
        slug: addForm.slug,
        status: addForm.status,
      });

      toast.success("State added!");
      setAddForm({ countryId: "", name: "", code: "", slug: "", status: true });
      setCurrentPage(1);
      fetchStates();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Error adding state");
    } finally {
      setAddLoading(false);
    }
  };

  // EDIT
  const openEdit = (row) => {
    setEditForm({
      _id: row._id,
      countryId: row.countryId?._id || row.countryId || "",
      name: row.name || "",
      code: row.code || "",
      slug: row.slug || "",
      status: row.status === true,
    });
    setEditOpen(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;

    if (name === "name") {
      setEditForm((p) => ({ ...p, name: value, slug: generateSlug(value) }));
      return;
    }
    setEditForm((p) => ({ ...p, [name]: value }));
  };

  const updateState = async () => {
    if (!editForm.countryId) return toast.error("Please select country");
    if (!editForm.name.trim()) return toast.error("Please enter state name");

    try {
      setEditLoading(true);
      await axiosInstance.put(`/admin/state/${editForm._id}`, {
        countryId: editForm.countryId,
        name: editForm.name.trim(),
        code: editForm.code.trim(),
        slug: editForm.slug,
        status: editForm.status,
      });

      toast.success("State updated!");
      setEditOpen(false);
      fetchStates();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Error updating state");
    } finally {
      setEditLoading(false);
    }
  };

  // DELETE
  const deleteState = (id) => {
    MySwal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this state!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        await axiosInstance.delete(`/admin/state/${id}`);
        toast.success("State deleted!");
        fetchStates();
      } catch (e) {
        toast.error(e?.response?.data?.message || "Error deleting state");
      }
    });
  };

  return (
    <>
      <Sidebar />
      <main className="content">
        <Navbar />

        <div className="py-4">
          <h1 className="h4">States (Add / Edit / Delete)</h1>
        </div>

        {/* ADD FORM */}
        <div className="card mb-4">
          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-md-3">
                <label className="form-label">Country</label>
                <select
                  className="form-control"
                  name="countryId"
                  value={addForm.countryId}
                  onChange={handleAddChange}
                >
                  <option value="">Select</option>
                  {countryOptions.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.name} ({c.iso2 || "-"})
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-3">
                <label className="form-label">State Name</label>
                <input className="form-control" name="name" value={addForm.name} onChange={handleAddChange} />
              </div>

              <div className="col-md-2">
                <label className="form-label">Code</label>
                <input className="form-control" name="code" value={addForm.code} onChange={handleAddChange} placeholder="DL / HR" />
              </div>

              <div className="col-md-2">
                <label className="form-label">Slug</label>
                <input className="form-control" value={addForm.slug} readOnly />
              </div>

              <div className="col-md-2">
                <label className="form-label">Status</label>
                <div className="d-flex gap-3">
                  <div className="form-check">
                    <input className="form-check-input" type="radio" checked={addForm.status === true} onChange={() => setAddForm((p) => ({ ...p, status: true }))} />
                    <label className="form-check-label text-success">Active</label>
                  </div>
                  <div className="form-check">
                    <input className="form-check-input" type="radio" checked={addForm.status === false} onChange={() => setAddForm((p) => ({ ...p, status: false }))} />
                    <label className="form-check-label text-danger">Inactive</label>
                  </div>
                </div>
              </div>

              <div className="col-12">
                <button className="btn btn-primary" type="button" onClick={addState} disabled={addLoading}>
                  {addLoading ? "Adding..." : "Add State"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* LIST */}
        <div className="card">
          <div className="table-responsive py-4">
            <div className="datatable-top px-3">
              <div className="datatable-dropdown">
                <label>
                  <select
                    className="datatable-selector"
                    value={limit}
                    onChange={(e) => {
                      setLimit(parseInt(e.target.value, 10));
                      setCurrentPage(1);
                    }}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={15}>15</option>
                    <option value={20}>20</option>
                    <option value={25}>25</option>
                  </select>{" "}
                  entries per page
                </label>
              </div>

              <div className="d-flex align-items-center gap-2">
                <select className="form-control" style={{ width: 240 }} value={filterCountryId} onChange={(e) => setFilterCountryId(e.target.value)}>
                  <option value="">All Countries</option>
                  {countryOptions.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.name}
                    </option>
                  ))}
                </select>

                <div className="datatable-search">
                  <input className="datatable-input" type="text" placeholder="Search state..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
                </div>

                <button className="btn btn-primary" onClick={onSearch}>Search</button>
              </div>
            </div>

            {loading ? (
              <div className="p-4">Loading...</div>
            ) : (
              <>
                <table className="table table-flush">
                  <thead>
                    <tr>
                      <th>State</th>
                      <th>Code</th>
                      <th>Slug</th>
                      <th>Country</th>
                      <th>Status</th>
                      <th style={{ width: 220 }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {states.map((row) => (
                      <tr key={row._id}>
                        <td>{row.name}</td>
                        <td>{row.code || "-"}</td>
                        <td>{row.slug || "-"}</td>
                        <td>{row.countryId?.name || "-"}</td>
                        <td>
                          <span className={`badge ${row.status ? "bg-success" : "bg-danger"}`}>
                            {row.status ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td>
                          <button className="btn btn-primary me-2" onClick={() => openEdit(row)}>Edit</button>
                          <button className="btn btn-danger" onClick={() => deleteState(row._id)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                    {!states.length && (
                      <tr>
                        <td colSpan={6} className="text-center py-4">No states found</td>
                      </tr>
                    )}
                  </tbody>
                </table>

                <div className="datatable-bottom px-3">
                  <nav className="datatable-pagination">
                    <ul className="datatable-pagination-list">
                      <li className={`datatable-pagination-list-item ${currentPage === 1 ? "datatable-hidden" : "datatable-active"}`}>
                        <button className="datatable-pagination-list-item-link" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>‹</button>
                      </li>
                      <li>
                        <div className="datatable-info py-1 px-3">
                          Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                        </div>
                      </li>
                      <li className={`datatable-pagination-list-item ${currentPage === totalPages ? "datatable-hidden" : "datatable-active"}`}>
                        <button className="datatable-pagination-list-item-link" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>›</button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </>
            )}
          </div>
        </div>

        {/* EDIT MODAL */}
        {editOpen && (
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ background: "rgba(0,0,0,.45)" }}>
            <div className="modal-dialog modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Edit State</h5>
                  <button type="button" className="btn-close" onClick={() => setEditOpen(false)} />
                </div>

                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Country</label>
                      <select className="form-control" name="countryId" value={editForm.countryId} onChange={handleEditChange}>
                        <option value="">Select</option>
                        {countryOptions.map((c) => (
                          <option key={c._id} value={c._id}>{c.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label">State Name</label>
                      <input className="form-control" name="name" value={editForm.name} onChange={handleEditChange} />
                    </div>

                    <div className="col-md-3">
                      <label className="form-label">Code</label>
                      <input className="form-control" name="code" value={editForm.code} onChange={handleEditChange} />
                    </div>

                    <div className="col-md-3">
                      <label className="form-label">Slug</label>
                      <input className="form-control" value={editForm.slug} readOnly />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label">Status</label>
                      <div className="d-flex gap-3">
                        <div className="form-check">
                          <input className="form-check-input" type="radio" checked={editForm.status === true} onChange={() => setEditForm((p) => ({ ...p, status: true }))} />
                          <label className="form-check-label text-success">Active</label>
                        </div>
                        <div className="form-check">
                          <input className="form-check-input" type="radio" checked={editForm.status === false} onChange={() => setEditForm((p) => ({ ...p, status: false }))} />
                          <label className="form-check-label text-danger">Inactive</label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setEditOpen(false)} disabled={editLoading}>Close</button>
                  <button className="btn btn-primary" onClick={updateState} disabled={editLoading}>
                    {editLoading ? "Updating..." : "Update"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </>
  );
}